import PageHeaderMeta from '@liveauctioneers/caterwaul-components/lib/PageHeaderMeta/PageHeaderMeta';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import isEqual from 'react-fast-compare';
import { useLocation } from 'react-router-dom';

import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryCatalogId,
    getItemSummaryImageVersion,
    getItemSummaryPhotos,
    getItemSummarySellerId,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getFirstSearchItemId } from '@/redux/modules/search/search.selectors';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import { getItemCardImageUrl, isSimilarItemsPage, logoUrl } from '@/utils/urls';

type Props = {
    baseUrl: string;
    page: number;
    searchTerm: string;
};

const SearchPageHeaderMeta = ({ baseUrl, page, searchTerm }: Props) => {
    const { pathname } = useLocation();
    const similarItemsPage = isSimilarItemsPage(pathname);
    const { formatMessage } = useIntl();
    let title = formatMessage(
        { id: similarItemsPage ? 'moreLikeResults' : 'searchTerms' },
        { searchTerm: cleanSearchTerms(searchTerm) }
    );

    if (page > 1) {
        title += formatMessage({ id: 'page_number_and_title' }, { page });
    }

    title = useAddNotificationCountToTitle(title);

    const description = formatMessage({ id: 'searchTermsDescription' }, { terms: cleanSearchTerms(searchTerm, true) });
    const canonical = `${baseUrl}/search`;

    const itemId = useAppSelector(getFirstSearchItemId);
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const imageVersion = useAppSelector((state) => getItemSummaryImageVersion(state, itemId));
    const photos = useAppSelector((state) => getItemSummaryPhotos(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const firstPhoto = photos?.[0] || 0;
    const imageUrl = getItemCardImageUrl(sellerId, catalogId, itemId, firstPhoto, imageVersion) || logoUrl;

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={description}
            imageUrl={imageUrl}
            title={title}
        >
            <meta
                content="noindex, nofollow"
                name="robots"
            />
        </PageHeaderMeta>
    );
};

export default React.memo<Props>(SearchPageHeaderMeta, isEqual);
